import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Send, Sparkles, Video, CheckCheck, MoreVertical, Ban, Flag } from "lucide-react";
import { toast } from "sonner";
import api, { resolveImg } from "../lib/api";
import { track } from "../lib/analytics";
import { useAuth } from "../context/AuthContext";
import { useNotifications } from "../context/NotificationContext";
import ProfileDetailModal from "../components/ProfileDetailModal";

const formatMsgTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
};

const dayKey = (iso) => {
  const d = new Date(iso);
  return isNaN(d.getTime()) ? "" : `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
};

const formatDateSeparator = (iso) => {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const now = new Date();
  const startOfDay = (x) => new Date(x.getFullYear(), x.getMonth(), x.getDate());
  const diffDays = Math.round((startOfDay(now) - startOfDay(d)) / 86400000);
  if (diffDays === 0) return "Aujourd'hui";
  if (diffDays === 1) return "Hier";
  if (diffDays < 7) return d.toLocaleDateString("fr-FR", { weekday: "long" });
  const sameYear = d.getFullYear() === now.getFullYear();
  return d.toLocaleDateString("fr-FR", { day: "numeric", month: "long", ...(sameYear ? {} : { year: "numeric" }) });
};

const Chat = () => {
  const { matchId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { refresh } = useNotifications() || {};
  const [messages, setMessages] = useState([]);
  const [limitReached, setLimitReached] = useState(false);
  const [quotaResetsAt, setQuotaResetsAt] = useState(null);
  const [quotaLimit, setQuotaLimit] = useState(3);
  const [awaitingReply, setAwaitingReply] = useState(false);
  const [text, setText] = useState("");
  const [profile, setProfile] = useState(null);
  const [sending, setSending] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [showReport, setShowReport] = useState(false);
  const [showBlock, setShowBlock] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const bottomRef = useRef(null);
  const scrollRef = useRef(null);

  const loadMessages = async () => {
    try {
      const { data } = await api.get(`/messages/${matchId}`);
      setMessages(data);
      if (data.some((m) => m.sender_id !== user?.user_id)) setAwaitingReply(false);
      if (!user?.premium) {
        try {
          const q = await api.get("/messages-quota");
          if (!q.data.reached) {
            setLimitReached(false);
            setQuotaResetsAt(null);
          } else {
            setQuotaResetsAt(q.data.resets_at || null);
          }
          setQuotaLimit(q.data.limit || 3);
        } catch (e) {}
      }
      refresh?.();
    } catch (e) {}
  };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/matches");
        const m = data.find((x) => x.match_id === matchId);
        if (m) setProfile(m.profile);
      } catch (e) {}
    })();
    loadMessages();
    const poll = setInterval(loadMessages, 15000);
    return () => clearInterval(poll);
    // eslint-disable-next-line
  }, [matchId]);

  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages]);

  // Auto-hide the limit banner as soon as the 24h rolling window frees up
  useEffect(() => {
    if (!limitReached || !quotaResetsAt) return;
    const ms = new Date(quotaResetsAt).getTime() - Date.now();
    if (ms <= 0) {
      setLimitReached(false);
      setQuotaResetsAt(null);
      return;
    }
    const t = setTimeout(() => {
      setLimitReached(false);
      setQuotaResetsAt(null);
    }, ms + 1000);
    return () => clearTimeout(t);
  }, [limitReached, quotaResetsAt]);

  const send = async () => {
    if (!text.trim()) return;
    setSending(true);
    const optimistic = text;
    setText("");
    try {
      await api.post("/messages", { match_id: matchId, text: optimistic });
      track("message_sent");
      setAwaitingReply(false);
      await loadMessages();
    } catch (e) {
      if (e.response?.status === 402) {
        setLimitReached(true);
        try {
          const q = await api.get("/messages-quota");
          setQuotaResetsAt(q.data.resets_at || null);
          setQuotaLimit(q.data.limit || 3);
        } catch (err) {}
        toast.error(`Limite de 3 messages gratuits/24h atteinte. Passez Premium pour continuer !`);
      } else if (e.response?.status === 409) {
        setAwaitingReply(true);
        toast.error(e.response?.data?.detail || "Attendez la réponse avant de continuer.");
      } else if (e.response?.status === 401) {
        toast.error("Votre session a expiré. Veuillez vous reconnecter.");
        navigate("/");
      } else {
        const detail = e.response?.data?.detail;
        const status = e.response?.status;
        toast.error(detail || (status ? `Message non envoyé (erreur ${status}).` : "Message non envoyé. Vérifiez votre connexion internet."));
      }
      setText(optimistic);
    } finally {
      setSending(false);
    }
  };

  const myReadMsgs = messages.filter((m) => m.sender_id === user?.user_id && m.read);
  const lastReadMineId = myReadMsgs.length ? myReadMsgs[myReadMsgs.length - 1].message_id : null;

  const doBlock = async () => {
    if (!profile || busy) return;
    setBusy(true);
    try {
      await api.post(`/users/${profile.user_id}/block`);
      toast.success(`${profile.name} a été bloqué(e).`);
      navigate("/matchs");
    } catch (e) {
      toast.error("Impossible de bloquer cet utilisateur.");
    }
    setBusy(false);
  };

  const doReport = async () => {
    if (!profile || busy) return;
    setBusy(true);
    try {
      await api.post(`/users/${profile.user_id}/report`, { reason });
      toast.success("Signalement envoyé. Merci de nous aider à garder KoraLove sûr.");
      setShowReport(false);
      setReason("");
    } catch (e) {
      toast.error("Échec de l'envoi du signalement.");
    }
    setBusy(false);
  };

  return (
    <div className="h-[100dvh] bg-kora-sand flex flex-col overflow-hidden">
      <header className="bg-white/85 backdrop-blur-xl border-b border-kora-line shrink-0 z-10">
        <div className="max-w-md mx-auto px-4 py-3 flex items-center gap-3">
          <button data-testid="chat-back" onClick={() => navigate("/matchs")} className="text-kora-ink">
            <ArrowLeft size={22} />
          </button>
          {profile && (
            <button data-testid="chat-header-profile" onClick={() => setShowProfile(true)} className="flex items-center gap-3 text-left">
              <div className="relative">
                <img src={resolveImg(profile.photo_url)} alt={profile.name} className="w-10 h-10 rounded-full object-cover" />
                {profile.presence?.online && (
                  <span className="absolute bottom-0 right-0 w-3 h-3 rounded-full bg-green-500 border-2 border-white" />
                )}
              </div>
              <div>
                <p className="font-display font-bold text-kora-ink leading-tight">{profile.name}</p>
                {profile.presence?.online ? (
                  <p data-testid="chat-online-status" className="text-xs text-green-600 font-semibold flex items-center gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" /> En ligne
                  </p>
                ) : profile.presence?.last_seen_minutes != null ? (
                  <p data-testid="chat-online-status" className="text-xs text-kora-muted">
                    En ligne il y a {profile.presence.last_seen_minutes < 60 ? `${profile.presence.last_seen_minutes} min` : `${Math.floor(profile.presence.last_seen_minutes / 60)} h`}
                  </p>
                ) : (
                  <p className="text-xs text-kora-sage">{profile.goal === "mariage" ? "Mariage" : "Amicale"}</p>
                )}
              </div>
            </button>
          )}
          <button
            data-testid="video-call-btn"
            onClick={() => {
              if (!user?.premium) {
                toast.error("L'appel vidéo est réservé aux membres Premium ✨");
                navigate("/premium");
                return;
              }
              navigate(`/call/${matchId}`);
            }}
            className="ml-auto w-10 h-10 rounded-full bg-kora-terracotta/10 text-kora-terracotta flex items-center justify-center hover:bg-kora-terracotta hover:text-white transition-colors"
          >
            <Video size={20} />
          </button>
          <div className="relative">
            <button
              data-testid="chat-menu-btn"
              onClick={() => setMenuOpen((o) => !o)}
              className="w-10 h-10 rounded-full text-kora-ink flex items-center justify-center hover:bg-kora-sand transition-colors"
            >
              <MoreVertical size={20} />
            </button>
            {menuOpen && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setMenuOpen(false)} />
                <div className="absolute right-0 top-11 z-20 w-48 bg-white rounded-2xl shadow-xl border border-kora-line py-1.5 overflow-hidden" data-testid="chat-menu">
                  <button data-testid="chat-report-btn" onClick={() => { setMenuOpen(false); setShowReport(true); }} className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-kora-ink hover:bg-kora-sand text-left">
                    <Flag size={16} className="text-kora-ochre" /> Signaler
                  </button>
                  <button data-testid="chat-block-btn" onClick={() => { setMenuOpen(false); setShowBlock(true); }} className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-red-500 hover:bg-red-50 text-left">
                    <Ban size={16} /> Bloquer
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </header>

      <div ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto kora-scroll flex flex-col">
        <div className="max-w-md w-full mx-auto px-4 py-4 space-y-2 mt-auto">
          {messages.length === 0 && (
            <div className="mt-8">
              <p className="text-center text-kora-muted text-sm">Envoyez le premier message à {profile?.name || "votre match"} 💬</p>
            </div>
          )}
          {messages.map((m, idx) => {
            const mine = m.sender_id === user?.user_id;
            const isLastMine = mine && m.message_id === lastReadMineId;
            const showDate = idx === 0 || dayKey(m.created_at) !== dayKey(messages[idx - 1].created_at);
            return (
              <React.Fragment key={m.message_id}>
                {showDate && (
                  <div className="flex justify-center my-3" data-testid="chat-date-separator">
                    <span className="text-[11px] font-semibold text-kora-muted bg-kora-line/50 rounded-full px-3 py-1">
                      {formatDateSeparator(m.created_at)}
                    </span>
                  </div>
                )}
                <div data-testid="chat-message" className={`flex flex-col ${mine ? "items-end" : "items-start"}`}>
                  <div className={`max-w-[75%] px-4 py-2.5 rounded-2xl text-sm ${mine ? "bg-kora-terracotta text-white rounded-br-md" : "bg-white text-kora-ink border border-kora-line rounded-bl-md"}`}>
                    {m.text}
                  </div>
                  <span className={`flex items-center gap-1 text-[11px] text-kora-muted mt-0.5 ${mine ? "mr-1" : "ml-1"}`}>
                    {formatMsgTime(m.created_at)}
                    {user?.premium && isLastMine && (
                      <span data-testid="read-receipt" className="flex items-center gap-0.5 text-kora-sage">
                        <CheckCheck size={13} /> Vu
                      </span>
                    )}
                  </span>
                </div>
              </React.Fragment>
            );
          })}
          <div ref={bottomRef} />
        </div>
      </div>

      {!user?.premium && limitReached && (
        <div className="max-w-md w-full mx-auto px-4 shrink-0">
          <button data-testid="chat-premium-cta" onClick={() => navigate("/premium")} className="w-full mb-2 flex flex-col items-center justify-center gap-0.5 bg-kora-ochre/15 text-[#C77826] rounded-2xl py-2 px-3 animate-kora-pulse">
            <span className="flex items-center gap-2 text-xs font-bold"><Sparkles size={14} /> Limite de {quotaLimit} messages gratuits/24h atteinte — Passez Premium</span>
            {quotaResetsAt && (
              <span data-testid="chat-limit-reset" className="text-[11px] font-medium opacity-80">
                Réinitialisation le {new Date(quotaResetsAt).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit" })} à {new Date(quotaResetsAt).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}
              </span>
            )}
          </button>
        </div>
      )}

      {awaitingReply && (
        <div className="max-w-md w-full mx-auto px-4 shrink-0">
          <div data-testid="chat-awaiting-banner" className="w-full mb-2 flex items-center justify-center gap-2 bg-blue-50 text-blue-600 rounded-full py-2 px-3 text-xs font-bold text-center">
            <Sparkles size={14} className="shrink-0" /> Premier message envoyé ! Attendez la réponse de {profile?.name || "votre contact"} avant de continuer.
          </div>
        </div>
      )}

      <div className="shrink-0 bg-white/90 backdrop-blur-xl border-t border-kora-line">
        <div className="max-w-md mx-auto px-4 py-3 flex items-center gap-2">
          <input
            data-testid="chat-input"
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && !awaitingReply && send()}
            disabled={awaitingReply}
            placeholder={awaitingReply ? "En attente de la réponse…" : "Votre message…"}
            className="flex-1 px-4 py-3 rounded-full border border-kora-line bg-kora-sand focus:outline-none focus:border-kora-terracotta disabled:opacity-60"
          />
          <button data-testid="chat-send" onClick={send} disabled={sending || awaitingReply} className="w-12 h-12 rounded-full bg-kora-terracotta text-white flex items-center justify-center shrink-0 disabled:opacity-60">
            <Send size={20} />
          </button>
        </div>
      </div>

      {/* Report modal */}
      {showReport && (
        <div className="fixed inset-0 z-[80] bg-kora-ink/70 backdrop-blur-sm flex items-end md:items-center justify-center p-4" data-testid="report-modal" onClick={() => setShowReport(false)}>
          <div className="bg-white w-full md:max-w-sm rounded-3xl p-6 animate-float-up" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 mb-1">
              <Flag size={18} className="text-kora-ochre" />
              <h3 className="font-display font-black text-lg text-kora-ink">Signaler {profile?.name}</h3>
            </div>
            <p className="text-sm text-kora-muted mb-3">Dites-nous ce qui ne va pas. Votre signalement reste confidentiel.</p>
            <textarea
              data-testid="report-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              placeholder="Motif (comportement, faux profil, contenu déplacé…)"
              className="w-full px-4 py-2.5 rounded-xl border border-kora-line focus:outline-none focus:border-kora-ochre resize-none text-sm"
            />
            <div className="flex gap-2 mt-4">
              <button data-testid="report-cancel" onClick={() => setShowReport(false)} className="flex-1 py-2.5 rounded-full border border-kora-line font-semibold text-kora-ink">Annuler</button>
              <button data-testid="report-submit" onClick={doReport} disabled={busy} className="flex-1 py-2.5 rounded-full bg-kora-ochre text-white font-bold disabled:opacity-60">Envoyer</button>
            </div>
          </div>
        </div>
      )}

      {/* Block confirm modal */}
      {showBlock && (
        <div className="fixed inset-0 z-[80] bg-kora-ink/70 backdrop-blur-sm flex items-end md:items-center justify-center p-4" data-testid="block-modal" onClick={() => setShowBlock(false)}>
          <div className="bg-white w-full md:max-w-sm rounded-3xl p-6 animate-float-up text-center" onClick={(e) => e.stopPropagation()}>
            <div className="w-14 h-14 rounded-full bg-red-50 text-red-500 flex items-center justify-center mx-auto mb-3">
              <Ban size={26} />
            </div>
            <h3 className="font-display font-black text-lg text-kora-ink">Bloquer {profile?.name} ?</h3>
            <p className="text-sm text-kora-muted mt-1 mb-5">Vous ne verrez plus cette personne et elle ne pourra plus vous contacter.</p>
            <div className="flex gap-2">
              <button data-testid="block-cancel" onClick={() => setShowBlock(false)} className="flex-1 py-2.5 rounded-full border border-kora-line font-semibold text-kora-ink">Annuler</button>
              <button data-testid="block-confirm" onClick={doBlock} disabled={busy} className="flex-1 py-2.5 rounded-full bg-red-500 text-white font-bold disabled:opacity-60">Bloquer</button>
            </div>
          </div>
        </div>
      )}
      {showProfile && profile && (
        <ProfileDetailModal profile={profile} onClose={() => setShowProfile(false)} />
      )}
    </div>
  );
};

export default Chat;